import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { articleImages } from '@/mocks/images';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const category = useMemo(
    () => categories.find((c) => c.slug === article?.category),
    [article]
  );

  const relatedArticles = useMemo(
    () =>
      articles
        .filter((a) => a.category === article?.category && a.slug !== slug)
        .slice(0, 3),
    [article, slug]
  );

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到" description="您访问的文章不存在或已被移除。" />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-file-unknow-line text-5xl text-foreground-300"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-800 mb-2">
              文章未找到
            </h1>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="px-6 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const imageUrl = articleImages[article.slug] || '';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: imageUrl,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    publisher: {
      '@type': 'Organization',
      name: '健康性知识科普平台',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://your-domain.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalPath={`/article/${article.slug}`}
        ogType="article"
        articleData={{
          publishedTime: article.date,
          modifiedTime: article.date,
          author: article.author,
          tags: article.tags,
        }}
        schema={articleSchema}
      />
      <Navbar />
      <main className="pt-16">
        <article>
          <div className="bg-background-100 border-b border-background-200/60">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
              <div className="mb-4">
                <Breadcrumb
                  items={[
                    { label: '首页', path: '/' },
                    { label: category?.name || '分类', path: `/category/${article.category}` },
                    { label: article.title },
                  ]}
                />
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-800 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400 mb-4">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  阅读 {article.readTime}
                </span>
                {category && (
                  <Link
                    to={`/category/${article.category}`}
                    className="flex items-center gap-1 text-primary-500 hover:text-primary-600"
                  >
                    <i className="ri-folder-line"></i>
                    {category.name}
                  </Link>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
            {imageUrl && (
              <div className="mb-8 rounded-lg overflow-hidden bg-background-100">
                <img
                  src={imageUrl}
                  alt={article.title}
                  className="w-full aspect-[16/9] object-cover"
                />
              </div>
            )}

            <div
              className="prose prose-lg max-w-none
                prose-headings:font-heading prose-headings:text-foreground-800
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-4
                prose-p:text-foreground-600 prose-p:leading-relaxed prose-p:mb-4
                prose-a:text-primary-500 prose-a:no-underline hover:prose-a:text-primary-600"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-800 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((related) => (
                  <ArticleCard key={related.slug} article={related} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}